import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, RefreshCw, Plus, FileText, Send, Trash2, Copy,
  Loader2, Sparkles, ArrowLeft, BellRing, Link2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import PlacesAutocompleteInput from "@/components/PlacesAutocompleteInput";
import api, { API } from "@/lib/api";
import useConfirmDialog from "@/lib/useConfirmDialog";
import NewQuoteDialog from "@/components/admin/NewQuoteDialog";
import QuoteDetailsDialog from "@/components/admin/QuoteDetailsDialog";

const FIELD =
  "bg-[#0a0a0a] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 h-11";

const VEHICLES = ["Comfort Class", "XL Vehicle (6+1)", "Van (8+1)", "Any / Recommend"];

const STATUS_COLOR = {
  draft: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  sent: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  accepted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  declined: "bg-red-500/15 text-red-300 border-red-500/30",
};

const fmtMoney = (n) => `€${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—");

const REMINDER_HOURS = 24;
const MAX_REMINDERS = 1;

function followUpDue(q) {
  if (q.status !== "sent") return false;
  if ((q.reminders_sent || 0) >= MAX_REMINDERS) return false;
  if (!q.pdf_sent_at) return false;
  const ageMs = Date.now() - new Date(q.pdf_sent_at).getTime();
  return ageMs >= REMINDER_HOURS * 3600 * 1000;
}

export default function AdminQuotes() {
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [reminderFor, setReminderFor] = useState(null); // quote object
  const [details, setDetails] = useState(null); // quote details modal

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("juke_admin_token");
        navigate("/admin");
        return;
      }
      toast.error("Could not load quotes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("juke_admin_token")) {
      navigate("/admin");
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    localStorage.removeItem("juke_admin_token");
    navigate("/admin");
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this quote?",
      description: "The quote, PDF share link, and any Stripe payment link will be permanently removed.",
      confirmLabel: "Delete quote",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Quote deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const sendEmail = async (q) => {
    if (!q.guest_email) return toast.error("No guest email on file.");
    try {
      const { data } = await api.post(`/admin/quotes/${q.id}/send`);
      toast.success(data.message || "Sent");
      setQuotes((qs) => qs.map((x) => (x.id === q.id ? { ...x, status: "sent", pdf_sent_at: new Date().toISOString() } : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
  };

  const handleReminderSent = (id, updates) => {
    setQuotes((qs) => qs.map((x) => (x.id === id ? { ...x, ...updates } : x)));
    setReminderFor(null);
  };

  const pdfUrl = (ref, token) => `${API}/quotes/${ref}/pdf?token=${encodeURIComponent(token)}`;
  const shareUrl = (ref, token) => `${window.location.origin}/quote/${ref}?t=${encodeURIComponent(token)}`;

  const copyShare = (ref, token) => {
    navigator.clipboard.writeText(shareUrl(ref, token)).then(() => toast.success("Share link copied"));
  };

  const createPaymentLink = async (q) => {
    try {
      const { data } = await api.post(`/admin/quotes/${q.id}/payment-link`);
      setQuotes((qs) => qs.map((x) => (x.id === q.id ? { ...x, stripe_checkout_url: data.url, payment_link_amount: data.amount } : x)));
      try { await navigator.clipboard.writeText(data.url); } catch { /* clipboard denied */ }
      toast.success(`Payment link created (€${Number(data.amount).toFixed(2)}) — copied to clipboard`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create payment link");
    }
  };

  const copyPaymentLink = async (q) => {
    if (!q.stripe_checkout_url) return;
    await navigator.clipboard.writeText(q.stripe_checkout_url);
    toast.success("Payment link copied");
  };

  return (
    <div data-testid="admin-quotes-page" className="min-h-screen bg-[#050505] text-zinc-100">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-zinc-400 hover:text-[#C5A059] transition-colors" data-testid="back-to-bookings">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <span className="font-display text-xl text-white">Juke</span>
            <span className="font-display text-xl text-[#C5A059] italic">Transfers</span>
            <span className="ml-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hidden sm:inline">Quote Studio</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/dashboard"
              className="hidden sm:inline-block text-sm text-zinc-400 hover:text-[#C5A059] px-3 py-2 transition-colors"
              data-testid="nav-bookings"
            >
              Bookings
            </Link>
            <button
              onClick={load}
              data-testid="refresh-btn"
              className="p-2 rounded-full hover:bg-white/5 text-zinc-400 hover:text-[#C5A059] transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300 hover:text-[#C5A059] text-sm transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-2">Quote Studio</p>
            <h1 className="font-display text-4xl text-white font-light">Branded quotes & PDFs</h1>
            <p className="text-sm text-zinc-500 mt-2">
              Generate a quote, share a PDF link, or send straight to the guest&apos;s inbox.
            </p>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="new-quote-btn"
            className="flex items-center gap-2 px-5 h-11 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold transition-all hover:shadow-[0_0_28px_rgba(197,160,89,0.4)]"
          >
            <Plus className="w-4 h-4" /> New Quote
          </button>
        </div>

        {/* Quotes Table */}
        <div className="rounded-xl border border-white/10 bg-[#0d0d0d] overflow-hidden">
          <div className="hidden lg:grid grid-cols-12 px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10">
            <div className="col-span-2">Reference</div>
            <div className="col-span-3">Guest</div>
            <div className="col-span-3">Route</div>
            <div className="col-span-1 text-right">Total</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {loading ? (
            <div className="px-6 py-12 text-center text-zinc-500">Loading…</div>
          ) : quotes.length === 0 ? (
            <div className="px-6 py-16 text-center" data-testid="empty-quotes">
              <Sparkles className="w-6 h-6 text-[#C5A059] mx-auto mb-3" />
              <p className="text-zinc-400">No quotes yet.</p>
              <p className="text-zinc-600 text-sm mt-2">Click &ldquo;New Quote&rdquo; to create your first branded PDF quote.</p>
            </div>
          ) : (
            quotes.map((q) => (
              <div
                key={q.id}
                data-testid={`quote-row-${q.ref_number}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-0 px-6 py-5 border-b border-white/5 hover:bg-white/[0.02] transition-colors"
              >
                <div className="lg:col-span-2">
                  <p className="text-[#C5A059] font-mono text-sm">{q.ref_number}</p>
                  <p className="text-xs text-zinc-600 mt-0.5">{fmtDate(q.created_at)}</p>
                </div>
                <div className="lg:col-span-3 cursor-pointer" data-testid={`quote-open-details-${q.ref_number}`} onClick={() => setDetails(q)}>
                  <p className="text-white hover:text-[#C5A059] transition-colors">{q.guest_name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{q.guest_email || q.guest_phone || "—"}</p>
                </div>
                <div className="lg:col-span-3 text-sm text-zinc-300">
                  <p className="truncate">{q.pickup}</p>
                  <p className="text-zinc-500 text-xs mt-0.5 truncate">→ {q.dropoff}</p>
                </div>
                <div className="lg:col-span-1 lg:text-right">
                  <p className="font-display text-lg text-white">{fmtMoney(q.total_price_eur)}</p>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-600">{q.vehicle_type}</p>
                </div>
                <div className="lg:col-span-1">
                  {q.paid ? (
                    <Badge
                      data-testid={`paid-badge-${q.ref_number}`}
                      className="bg-emerald-500/15 border border-emerald-400/60 text-emerald-300 uppercase tracking-wider text-[10px] px-3 py-1"
                    >
                      ✓ Paid
                    </Badge>
                  ) : (
                    <Badge variant="outline" className={`${STATUS_COLOR[q.status]} uppercase tracking-wider text-[10px] px-3 py-1`}>
                      {q.status}
                    </Badge>
                  )}
                  {q.paid && q.paid_amount_eur ? (
                    <span className="block mt-1.5 text-[9px] uppercase tracking-[0.18em] text-emerald-400">
                      €{Number(q.paid_amount_eur).toFixed(2)} · {q.paid_mode === "full" ? "full" : "deposit"}
                    </span>
                  ) : null}
                  {followUpDue(q) && (
                    <span
                      data-testid={`followup-badge-${q.ref_number}`}
                      className="block mt-1.5 text-[9px] uppercase tracking-[0.18em] text-[#C5A059] font-semibold"
                    >
                      Needs follow-up
                    </span>
                  )}
                </div>
                <div className="lg:col-span-2 flex items-center lg:justify-end gap-2 flex-wrap">
                  <a
                    href={pdfUrl(q.ref_number, q.share_token)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`pdf-${q.ref_number}`}
                    className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-[#C5A059] border border-white/10 hover:border-[#C5A059]/40 rounded-full px-3 py-1.5 transition-colors"
                    title="Open PDF"
                  >
                    <FileText className="w-3.5 h-3.5" /> PDF
                  </a>
                  <button
                    onClick={() => copyShare(q.ref_number, q.share_token)}
                    data-testid={`copy-${q.ref_number}`}
                    className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-[#C5A059] border border-white/10 hover:border-[#C5A059]/40 rounded-full px-3 py-1.5 transition-colors"
                    title="Copy share link"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => sendEmail(q)}
                    data-testid={`send-${q.ref_number}`}
                    disabled={!q.guest_email}
                    className="flex items-center gap-1.5 text-xs text-[#C5A059] border border-[#C5A059]/40 hover:bg-[#C5A059]/10 rounded-full px-3 py-1.5 transition-colors disabled:opacity-30"
                    title="Send to guest"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                  {(q.deposit_mode && q.deposit_mode !== "default") && (
                    q.stripe_checkout_url ? (
                      <button
                        onClick={() => copyPaymentLink(q)}
                        data-testid={`pay-link-copy-${q.ref_number}`}
                        className="flex items-center gap-1.5 text-xs text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 rounded-full px-3 py-1.5 transition-colors"
                        title="Copy Stripe payment link"
                      >
                        <Link2 className="w-3.5 h-3.5" /> €{Number(q.payment_link_amount || 0).toFixed(0)}
                      </button>
                    ) : (
                      <button
                        onClick={() => createPaymentLink(q)}
                        data-testid={`pay-link-${q.ref_number}`}
                        className="flex items-center gap-1.5 text-xs text-zinc-300 border border-white/10 hover:border-[#C5A059]/40 hover:text-[#C5A059] rounded-full px-3 py-1.5 transition-colors"
                        title="Create Stripe payment link for the deposit"
                      >
                        <Link2 className="w-3.5 h-3.5" /> Pay-link
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setReminderFor(q)}
                    data-testid={`remind-${q.ref_number}`}
                    disabled={!q.guest_email || q.status !== "sent" || (q.reminders_sent || 0) >= MAX_REMINDERS}
                    className={`flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 transition-colors border ${
                      followUpDue(q)
                        ? "text-black bg-[#C5A059] border-[#C5A059] hover:bg-[#D4AF37] animate-pulse"
                        : "text-zinc-400 border-white/10 hover:text-[#C5A059] hover:border-[#C5A059]/40"
                    } disabled:opacity-30 disabled:animate-none disabled:bg-transparent disabled:text-zinc-600`}
                    title={(q.reminders_sent || 0) >= MAX_REMINDERS ? "Reminder already sent" : q.status !== "sent" ? "Available once quote is sent" : "Send follow-up"}
                  >
                    <BellRing className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(q.id)}
                    data-testid={`delete-${q.ref_number}`}
                    className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      <NewQuoteDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={(q) => {
          setQuotes((qs) => [q, ...qs]);
          setOpen(false);
          toast.success(`Quote ${q.ref_number} created — ${fmtMoney(q.total_price_eur)}`);
        }}
      />

      {details && (
        <QuoteDetailsDialog
          quote={details}
          onClose={() => setDetails(null)}
          onUpdated={(u) => {
            setDetails(u);
            setQuotes((qs) => qs.map((x) => (x.id === u.id ? { ...x, ...u } : x)));
          }}
        />
      )}

      <ReminderDialog
        quote={reminderFor}
        onClose={() => setReminderFor(null)}
        onSent={handleReminderSent}
      />
      <ConfirmDialog />
    </div>
  );
}

function ReminderDialog({ quote, onClose, onSent }) {
  const [discount, setDiscount] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { if (quote) setDiscount(""); }, [quote]);

  const submit = async () => {
    if (!quote) return;
    const pct = discount === "" ? 0 : Number(discount);
    if (Number.isNaN(pct) || pct < 0 || pct > 50) {
      return toast.error("Discount must be between 0 and 50%.");
    }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/quotes/${quote.id}/remind`, {
        discount_percent: pct || null,
      });
      toast.success(data.message || "Reminder sent");
      onSent(quote.id, {
        reminders_sent: (quote.reminders_sent || 0) + 1,
        last_reminder_at: new Date().toISOString(),
        last_discount_percent: pct || null,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reminder failed");
    } finally {
      setSending(false);
    }
  };

  const open = !!quote;
  const newTotal = quote && discount
    ? (Number(quote.total_price_eur) * (1 - Number(discount) / 100)).toFixed(2)
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reminder-dialog" className="bg-[#0d0d0d] border-white/10 text-zinc-100 max-w-md">
        {quote && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-white font-light">Send follow-up</DialogTitle>
              <DialogDescription className="text-zinc-500">
                To {quote.guest_name} · {quote.ref_number} · {fmtMoney(quote.total_price_eur)} original
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">
                  Optional discount (%)
                </Label>
                <Input
                  data-testid="reminder-discount"
                  type="number"
                  min={0}
                  max={50}
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  placeholder="Leave blank for no discount"
                  className={`${FIELD} mt-2`}
                />
                <p className="text-xs text-zinc-500 mt-2">
                  {newTotal
                    ? <>Reminder will offer <b className="text-[#C5A059]">€{newTotal}</b> (was {fmtMoney(quote.total_price_eur)}).</>
                    : "No discount — sends a polite check-in only."}
                </p>
              </div>

              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-xs text-zinc-500 leading-relaxed">
                The PDF will be re-attached. After this single nudge, no further automated reminders will be sent — you can still reach the guest by phone or WhatsApp.
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 h-11 rounded-full border border-white/10 text-zinc-300 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  data-testid="reminder-submit"
                  disabled={sending}
                  className="flex-1 h-11 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><BellRing className="w-4 h-4" /> Send reminder</>}
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
      {children}
    </div>
  );
}
